import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Quote } from '../../services/carrierApi';

/* ================================================================
   Pieces shared by the three dashboards, so a carrier screen and a
   NowCerts screen stay the same object with different columns.
   Class names are the portal's existing db-* family.
   ================================================================ */

const avatarColors = [
  '#1a7a3a', '#0d6efd', '#6f42c1', '#d97706',
  '#0891b2', '#be185d', '#059669', '#7c3aed',
];

export function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return avatarColors[Math.abs(hash) % avatarColors.length];
}

export function getInitials(name: string): string {
  return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

export function statusBadgeClass(status: string): string {
  const key = status.toLowerCase().replace(/\s+/g, '-');
  const map: Record<string, string> = {
    approved: 'badge badge--approved',
    bound: 'badge badge--bound',
    pending: 'badge badge--pending',
    'in-review': 'badge badge--review',
    declined: 'badge badge--rejected',
    cancelled: 'badge badge--rejected',
    rejected: 'badge badge--rejected',
  };
  return map[key] || 'badge badge--pending';
}

export interface StatSpec {
  label: string;
  value: number | string;
  accent: string;
  bg: string;
  icon: React.ReactNode;
  hint?: string;
}

export const StatGrid: React.FC<{ stats: StatSpec[] }> = ({ stats }) => (
  <div className="db-stats">
    {stats.map((s, i) => (
      <motion.div
        key={s.label}
        className="db-stat"
        style={{ '--accent': s.accent, '--accent-bg': s.bg } as React.CSSProperties}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: i * 0.07, duration: 0.35, ease: 'easeOut' }}
      >
        <div className="db-stat__top-bar" />
        <div className="db-stat__body">
          <div className="db-stat__icon-wrap">{s.icon}</div>
          <div className="db-stat__info">
            <span className="db-stat__label">{s.label}</span>
            <span className="db-stat__value">{s.value}</span>
          </div>
        </div>
        {s.hint && (
          <div className="db-stat__footer">
            <span className="db-stat__trend-label">{s.hint}</span>
          </div>
        )}
      </motion.div>
    ))}
  </div>
);

export const CapabilityList: React.FC<{ title: string; items: string[] }> = ({ title, items }) => (
  <div className="cap-card">
    <h3 className="cap-card__title">{title}</h3>
    <ul className="cap-list">
      {items.map(item => (
        <li key={item} className="cap-list__item">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

export interface QuoteTableProps {
  quotes: Quote[];
  title: string;
  subtitle: string;
  showCarrier?: boolean;
  showAssignee?: boolean;
  assigneeName?: (userId?: string) => string;
  /** Custom Assigned To cell content; takes precedence over assigneeName. */
  renderAssignee?: (quote: Quote) => React.ReactNode;
  emptyLabel?: React.ReactNode;
  onRowAction?: (quote: Quote) => void;
  actionLabel?: string;
  /** No quote-detail endpoint for carrier quotes yet — show the state plainly. */
  comingSoon?: boolean;
  /** Adds a "Details" column with a View Details button on every row. */
  showDetails?: boolean;
  onViewDetails?: (quote: Quote) => void;
  headerRight?: React.ReactNode;
  /** Replaces the default "Showing N quotes" footer. */
  footer?: React.ReactNode;
}

export const QuoteTable: React.FC<QuoteTableProps> = ({
  quotes,
  title,
  subtitle,
  showCarrier = false,
  showAssignee = false,
  assigneeName,
  renderAssignee,
  emptyLabel = 'No records found',
  onRowAction,
  actionLabel = 'View details',
  comingSoon = false,
  showDetails = false,
  onViewDetails,
  headerRight,
  footer,
}) => (
  <motion.div
    className="db-table-card"
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.2, duration: 0.4, ease: 'easeOut' }}
  >
    <div className="db-table-card__header">
      <div>
        <h2 className="db-table-card__title">{title}</h2>
        <p className="db-table-card__sub">{subtitle}</p>
      </div>
      {headerRight}
    </div>

    <div className="db-table-wrap">
      <table className="db-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Insured Name</th>
            {showCarrier && <th>Carrier</th>}
            <th>Status</th>
            <th>Created Date</th>
            {showAssignee && <th>Assigned To</th>}
            {comingSoon ? <th>Details</th> : onRowAction && <th />}
            {showDetails && <th>Details</th>}
          </tr>
        </thead>
        <tbody>
          {quotes.length === 0 && (
            <tr>
              <td colSpan={4 + Number(showCarrier) + Number(showAssignee) + Number(comingSoon || !!onRowAction) + Number(showDetails)}>
                <div className="db-empty">
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                  <p>{emptyLabel}</p>
                </div>
              </td>
            </tr>
          )}
          {quotes.map(q => (
            <tr key={q.id} className="db-table__row">
              <td><span className="quote-id">{q.id}</span></td>
              <td>
                <div className="company-cell">
                  <span
                    className="company-cell__avatar"
                    style={{ background: getAvatarColor(q.insuredName) }}
                  >
                    {getInitials(q.insuredName)}
                  </span>
                  <div className="company-cell__info">
                    <span className="company-cell__name">{q.insuredName}</span>
                  </div>
                </div>
              </td>
              {showCarrier && (
                <td><span className="carrier-chip">{q.carrierName || '—'}</span></td>
              )}
              <td><span className={statusBadgeClass(q.status)}>{q.status}</span></td>
              <td><span className="db-date">{q.createdDate}</span></td>
              {showAssignee && (
                <td>
                  {renderAssignee ? renderAssignee(q) : (
                    <span className="db-date">
                      {assigneeName ? assigneeName(q.assignedToUserId) : q.assignedToUserId || 'Unassigned'}
                    </span>
                  )}
                </td>
              )}
              {comingSoon ? (
                <td>
                  <span className="soon-chip" title="Quote details for carrier submissions are not available yet">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 7 12 12 15 14" />
                    </svg>
                    Coming soon
                  </span>
                </td>
              ) : onRowAction && (
                <td>
                  <button
                    className="db-action-btn"
                    title={actionLabel}
                    onClick={() => onRowAction(q)}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </button>
                </td>
              )}
              {showDetails && (
                <td>
                  <button
                    type="button"
                    className="qh-view-btn"
                    onClick={() => onViewDetails?.(q)}
                  >
                    View Details
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <div className="db-table-footer">
      {footer ?? (
        <span className="db-table-footer__info">
          Showing <strong>{quotes.length}</strong> {quotes.length === 1 ? 'quote' : 'quotes'}
        </span>
      )}
    </div>
  </motion.div>
);

export const PAGE_SIZES = [10, 15, 20, 50, 100];

/** Page numbers to show: first, last, and the current page with one neighbour
 *  each side; null marks a gap. */
function pageWindow(current: number, total: number): (number | null)[] {
  const pages = [1, current - 1, current, current + 1, total]
    .filter((p, i, all) => p >= 1 && p <= total && all.indexOf(p) === i)
    .sort((a, b) => a - b);
  return pages.flatMap((p, i) => (i > 0 && p - pages[i - 1] > 1 ? [null, p] : [p]));
}

/** Server-driven paging: the caller owns the data. */
export interface RemotePaging {
  /** Rows across all pages. */
  total: number;
  /** The table is about to show rows [start, start + size) — fetch them if they aren't held yet.
   *  Keep this reference stable (useCallback), it re-runs whenever it changes. */
  onRange: (start: number, size: number) => void;
}

/** QuoteTable with a rows-per-page picker. Pages `quotes` client-side, unless `remote` is given:
 *  then `quotes` is already just the current page's rows and the table only reports which
 *  range it needs, so the full list never has to be loaded. */
export const PaginatedQuoteTable: React.FC<
  Omit<QuoteTableProps, 'footer'> & { defaultPageSize?: number; remote?: RemotePaging }
> = ({ quotes, defaultPageSize = 10, remote, ...tableProps }) => {
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [page, setPage] = useState(1);

  const total = remote ? remote.total : quotes.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, totalPages);
  const start = (current - 1) * pageSize;

  // A new result set (search, reload) starts back on page 1.
  useEffect(() => setPage(1), [total]);

  const onRange = remote?.onRange;
  useEffect(() => { onRange?.(start, pageSize); }, [onRange, start, pageSize]);

  const footer = (
    <>
      <span className="db-table-footer__info">
        Showing <strong>{total === 0 ? 0 : start + 1}–{Math.min(start + pageSize, total)}</strong> of{' '}
        <strong>{total}</strong> {total === 1 ? 'quote' : 'quotes'}
      </span>
      <div className="db-table-footer__controls">
        <label className="db-page-size">
          Rows per page
          <select
            value={pageSize}
            onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
          >
            {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <div className="db-pagination">
          <button
            className="page-btn"
            aria-label="Previous page"
            disabled={current === 1}
            onClick={() => setPage(current - 1)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          {pageWindow(current, totalPages).map((p, i) =>
            p === null ? (
              <span key={`gap-${i}`} className="page-btn page-btn--ellipsis">…</span>
            ) : (
              <button
                key={p}
                className={`page-btn${p === current ? ' page-btn--active' : ''}`}
                aria-current={p === current ? 'page' : undefined}
                onClick={() => setPage(p)}
              >
                {p}
              </button>
            )
          )}
          <button
            className="page-btn"
            aria-label="Next page"
            disabled={current === totalPages}
            onClick={() => setPage(current + 1)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </button>
        </div>
      </div>
    </>
  );

  return <QuoteTable {...tableProps} quotes={remote ? quotes : quotes.slice(start, start + pageSize)} footer={footer} />;
};

export const icons = {
  doc: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  ),
  clock: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  check: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  ),
  building: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="9" y1="3" x2="9" y2="21" />
      <line x1="13" y1="8" x2="17" y2="8" />
      <line x1="13" y1="12" x2="17" y2="12" />
    </svg>
  ),
  users: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    </svg>
  ),
  shield: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  progress: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  ),
};
